import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList, regionOptions, yearOptions, sortOptions } from "@/mocks/anime";

const PAGE_SIZE = 8;

type FilterOption = { label: string; value: string };

function FilterRow({
  label,
  options,
  active,
  onChange,
}: {
  label: string;
  options: string[];
  active: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-4 last:border-b-0 sm:flex-row sm:items-center">
      <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer ${
              active === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Category() {
  const { type = "all" } = useParams();
  const decodedType = decodeURIComponent(type);
  const isAll = decodedType === "all" || decodedType === "全部";
  const displayType = isAll ? "全部" : decodedType;

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("最新");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [decodedType, region, year, sort]);

  const filtered = useMemo(() => {
    const list = animeList.filter((anime) => {
      const matchGenre = isAll || anime.genres.includes(decodedType);
      const matchRegion = region === "全部" || anime.region === region;
      const matchYear = year === "全部" || String(anime.year) === year;
      return matchGenre && matchRegion && matchYear;
    });

    const sorted = [...list];
    if (sort === "最热") {
      sorted.sort((a, b) => b.views - a.views);
    } else if (sort === "评分") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    }
    return sorted;
  }, [decodedType, isAll, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const sortOptionsForView: FilterOption[] = sortOptions.map((item) => ({ label: item, value: item }));

  return (
    <Layout>
      <Seo
        title={`${displayType}动漫在线观看 - 樱花动漫`}
        description={`樱花动漫${displayType}动漫专区，涵盖热门与经典${displayType}题材动画作品，支持在线观看完整版，持续更新最新集数。`}
        keywords={`${displayType}动漫,${displayType}动画在线观看,樱花动漫,完整版动漫`}
        path={`/category/${type}`}
      />

      <div className="w-full px-4 pt-8 md:px-6 lg:px-10">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category/all" className="hover:text-primary-600">动漫分类</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{displayType}</span>
        </nav>

        <div className="mt-4">
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{displayType}动漫</h1>
          <p className="mt-2 text-sm text-foreground-500">
            共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 部作品，按题材、地区与年份自由筛选
          </p>
        </div>

        <div className="mt-6 rounded-lg border border-background-200 bg-background-100 px-5 py-2 md:px-6">
          <FilterRow label="地区" options={regionOptions} active={region} onChange={setRegion} />
          <FilterRow label="年份" options={yearOptions} active={year} onChange={setYear} />
          <FilterRow
            label="排序"
            options={sortOptionsForView.map((item) => item.label)}
            active={sort}
            onChange={setSort}
          />
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {pageItems.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>

            {totalPages > 1 ? (
              <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页">
                <button
                  type="button"
                  disabled={safePage === 1}
                  onClick={() => setPage(safePage - 1)}
                  className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setPage(num)}
                    className={`flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                      num === safePage
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={safePage === totalPages}
                  onClick={() => setPage(safePage + 1)}
                  className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  下一页
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </nav>
            ) : null}
          </>
        ) : (
          <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <h2 className="mt-5 text-lg font-semibold text-foreground-950">暂时没有符合条件的结果</h2>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              换一个筛选条件试试，或者返回首页看看其他正在更新的动漫吧。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}